// Shared PDF geometry/color constants and low-level jsPDF drawing helpers
// used by drawSlide.js. Kept content-agnostic so slide copy (slidesData.js)
// can change independently of how it's laid out on the page.
export const BG = [10, 10, 10];
export const WHITE = [255, 255, 255];
export const GRAY = [150, 150, 150];
export const DIM = [90, 90, 90];
export const ACCENT = [0, 229, 255];
export const PALETTE = [ACCENT, [255, 45, 120], [230, 192, 104]];

export const W = 297;
export const H = 210;
export const MX = 22;
export const CONTENT_W = W - MX * 2;

// Loads an image (same-origin or CORS-enabled) and returns a JPEG data URL
// plus its natural pixel size, so it can be embedded and aspect-fit in the PDF.
export function loadImageBox(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.88), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

export function wrap(doc, text, maxWidth) {
  return doc.splitTextToSize(text, maxWidth);
}

export function drawStats(doc, items, y, contentW) {
  const colW = contentW / items.length;
  items.forEach(([v, t], i) => {
    const x = MX + i * colW;
    const color = PALETTE[i % PALETTE.length];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...color);
    doc.text(v, x, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    const lines = wrap(doc, t, colW - 10);
    doc.text(lines, x, y + 8);
  });
}

export function drawRow(doc, items, y, contentW) {
  const colW = contentW / items.length;
  items.forEach(([w, d, today], i) => {
    const x = MX + i * colW;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(colW < 40 ? 12 : 14);
    doc.setTextColor(...ACCENT);
    doc.text(w, x, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    doc.text(wrap(doc, d, colW - 10), x, y + 7);
    if (today !== undefined) {
      const badgeY = y + 16;
      const badgeLabel = today ? "TODAY" : "CONCEPT";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      const bw = doc.getTextWidth(badgeLabel) + 6;
      if (today) {
        doc.setFillColor(...ACCENT);
        doc.roundedRect(x, badgeY - 4, bw, 6, 3, 3, "F");
        doc.setTextColor(0, 0, 0);
      } else {
        doc.setDrawColor(90, 90, 90);
        doc.roundedRect(x, badgeY - 4, bw, 6, 3, 3, "S");
        doc.setTextColor(...DIM);
      }
      doc.text(badgeLabel, x + 3, badgeY);
    }
  });
}

export function drawBadges(doc, items, y, contentW) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  let x = MX;
  const rowH = 11;
  let curY = y;
  items.forEach((label) => {
    const tw = doc.getTextWidth(label);
    const chipW = tw + 12;
    if (x + chipW > MX + contentW) {
      x = MX;
      curY += rowH + 4;
    }
    doc.setDrawColor(90, 90, 90);
    doc.roundedRect(x, curY - 6.5, chipW, 9, 4, 4, "S");
    doc.setTextColor(...WHITE);
    doc.text(label, x + 6, curY);
    x += chipW + 5;
  });
  return curY;
}

// jsPDF's built-in fonts don't support the Unicode arrow glyph (\u2192), which
// rendered as garbled text. Draw a small vector chevron instead so arrow-chain
// copy (e.g. the ICP slide's "A -> B -> C") always renders correctly.
export function drawArrowGlyph(doc, x, y, color) {
  doc.setFillColor(...color);
  doc.triangle(x, y - 1.4, x, y + 1.4, x + 2.8, y, "F");
}

export function drawArrowChain(doc, segments, x, y, contentW) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  let curX = x;
  segments.forEach((seg, i) => {
    doc.setTextColor(...ACCENT);
    doc.text(seg, curX, y);
    curX += doc.getTextWidth(seg);
    if (i < segments.length - 1) {
      curX += 4;
      drawArrowGlyph(doc, curX, y - 1.6, ACCENT);
      curX += 7;
    }
  });
}

// Vector check / X marks (not font glyphs) so the Landscape table's Worn /
// Delivers / Senses columns visually match the on-screen Check/X icons
// with zero risk of an unsupported Unicode symbol rendering as garbage.
export function drawCheckGlyph(doc, x, y, color) {
  doc.setDrawColor(...color);
  doc.setLineWidth(1.1);
  doc.line(x - 3, y, x - 0.8, y + 2.4);
  doc.line(x - 0.8, y + 2.4, x + 3.4, y - 3.2);
}

export function drawXGlyph(doc, x, y, color) {
  doc.setDrawColor(...color);
  doc.setLineWidth(1.1);
  doc.line(x - 2.4, y - 2.4, x + 2.4, y + 2.4);
  doc.line(x - 2.4, y + 2.4, x + 2.4, y - 2.4);
}

export function drawList(doc, items, y, extra, contentW, footerMaxY) {
  const GAP = 12;
  const DESC_GAP = 6;

  // Measure the list's natural height first (same font sequence as the real
  // draw pass below) so spacing can be compressed if the content would
  // otherwise run into a fixed-position link or off the bottom of the page.
  let naturalHeight = 0;
  items.forEach(([title, desc]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    const titleLines = wrap(doc, title, contentW - 6);
    naturalHeight += (titleLines.length - 1) * 7;
    if (desc) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const lines = wrap(doc, desc, contentW - 6);
      naturalHeight += DESC_GAP + (lines.length - 1) * 5;
    }
    naturalHeight += GAP;
  });

  const ceiling = footerMaxY ? footerMaxY - 6 : H - 28;
  const available = ceiling - y;
  const scale = available > 0 && naturalHeight > available ? Math.max(0.55, available / naturalHeight) : 1;
  const gap = GAP * scale;
  const descGap = DESC_GAP * scale;

  let curY = y;
  items.forEach(([title, desc, url]) => {
    doc.setFillColor(...ACCENT);
    doc.circle(MX + 1.2, curY - 1.5, 1.1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    const titleLines = wrap(doc, title, contentW - 6);
    if (url) {
      doc.setTextColor(...ACCENT);
      doc.textWithLink(title, MX + 6, curY, { url, maxWidth: contentW - 6 });
    } else {
      doc.setTextColor(...WHITE);
      doc.text(titleLines, MX + 6, curY);
    }
    curY += (titleLines.length - 1) * 7;
    if (desc) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...GRAY);
      const lines = wrap(doc, desc, contentW - 6);
      doc.text(lines, MX + 6, curY + descGap);
      curY += descGap + (lines.length - 1) * 5;
    }
    curY += gap;
  });
  const footerY = footerMaxY ? Math.min(curY + 3, footerMaxY) : curY + 3;
  if (extra.footerSegments) {
    drawArrowChain(doc, extra.footerSegments, MX, footerY, contentW);
  } else if (extra.footer) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...ACCENT);
    if (extra.footerLink) {
      doc.textWithLink(extra.footer, MX, footerY, { url: extra.footerLink });
    } else {
      doc.text(extra.footer, MX, footerY);
    }
  }
}

export function drawImage(doc, image) {
  const boxX = W - MX - image.colW;
  const boxY = 40;
  const boxMaxW = image.colW;
  const boxMaxH = H - 14 - 12 - boxY;
  const ratio = Math.min(boxMaxW / image.w, boxMaxH / image.h);
  const drawW = image.w * ratio;
  const drawH = image.h * ratio;
  const drawX = boxX + (boxMaxW - drawW) / 2;
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.4);
  doc.roundedRect(drawX - 1.5, boxY - 1.5, drawW + 3, drawH + 3, 2, 2, "S");
  doc.addImage(image.dataUrl, "JPEG", drawX, boxY, drawW, drawH);
}

export function drawTable(doc, extra, y, contentW) {
  const { columns, rows } = extra;
  const labelW = contentW * 0.46;
  const colW = (contentW - labelW) / (columns.length - 1);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...DIM);
  columns.forEach((c, i) => {
    if (i === 0) return;
    const x = MX + labelW + (i - 1) * colW + colW / 2;
    doc.text(c.toUpperCase(), x, y, { align: "center" });
  });
  let curY = y + 8;
  const rowH = 11;
  rows.forEach((r, ri) => {
    const highlight = r.highlight;
    if (highlight) {
      doc.setFillColor(20, 32, 34);
      doc.rect(MX - 2, curY - 7, contentW + 4, rowH, "F");
    }
    doc.setFont("helvetica", highlight ? "bold" : "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...(highlight ? WHITE : GRAY));
    doc.text(r.label, MX, curY);
    r.values.forEach((v, i) => {
      const x = MX + labelW + i * colW + colW / 2;
      if (v) drawCheckGlyph(doc, x, curY - 2, ACCENT);
      else drawXGlyph(doc, x, curY - 2, DIM);
    });
    curY += rowH;
    if (ri < rows.length - 1) {
      doc.setDrawColor(40, 40, 40);
      doc.line(MX, curY - rowH + 4, MX + contentW, curY - rowH + 4);
    }
  });
}
